import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateEmployee, useUpdateEmployee, useDepartments, useLevels } from '@/hooks'
import { api } from '@/api'
import { Employee, EmployeeStatus, Gender } from '@/types'
import { toast } from 'sonner'

interface EmployeeFormProps {
  employee: Employee | null
  onClose: () => void
  onSuccess: () => void
}

export function EmployeeForm({ employee, onClose, onSuccess }: EmployeeFormProps) {
  const isEditing = !!employee
  const [formData, setFormData] = useState({
    id: employee?.id || '',
    name: employee?.name || '',
    gender: employee?.gender || null as Gender | null,
    birth_date: employee?.birth_date || '',
    level_code: employee?.level_code || '',
    dept_code: employee?.dept_code || '',
    manager_id: employee?.manager_id || '',
    entry_date: employee?.entry_date || '',
    status: employee?.status || '在职' as EmployeeStatus,
    phone: employee?.phone || '',
    email: employee?.email || '',
  })

  const { data: departments } = useDepartments()
  const { data: levels } = useLevels()
  const createEmployee = useCreateEmployee()
  const updateEmployee = useUpdateEmployee()

  // 根据选择的部门获取该部门员工作为汇报人选项（仅在选择部门后发起请求）
  const { data: deptEmployees } = useQuery({
    queryKey: ['employees', 'managers', formData.dept_code],
    queryFn: () => api.getEmployees({
      page_size: 100,
      dept_code: formData.dept_code!,
      status: '正式',
    }),
    enabled: !!formData.dept_code,
  })

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.level_code || !formData.dept_code || !formData.entry_date) {
      toast.error('请填写必填字段')
      return
    }

    try {
      if (isEditing && employee) {
        await updateEmployee.mutateAsync({
          employeeId: employee.id,
          data: {
            name: formData.name,
            gender: formData.gender,
            birth_date: formData.birth_date || null,
            level_code: formData.level_code,
            dept_code: formData.dept_code,
            manager_id: formData.manager_id || null,
            entry_date: formData.entry_date,
            status: formData.status,
            phone: formData.phone || null,
            email: formData.email || null,
          },
        })
        toast.success('员工信息已更新')
      } else {
        await createEmployee.mutateAsync({
          id: formData.id,
          name: formData.name,
          gender: formData.gender,
          birth_date: formData.birth_date || null,
          level_code: formData.level_code,
          dept_code: formData.dept_code,
          manager_id: formData.manager_id || null,
          entry_date: formData.entry_date,
          status: formData.status,
          phone: formData.phone || null,
          email: formData.email || null,
        })
        toast.success('员工已创建')
      }
      onSuccess()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失败')
    }
  }, [formData, isEditing, employee, createEmployee, updateEmployee, onSuccess])

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value }
      // When changing department, reset manager
      if (field === 'dept_code') {
        newData.manager_id = ''
      }
      return newData
    })
  }

  // Filter out current employee from manager options
  const availableManagers = deptEmployees?.items?.filter(emp => emp.id !== employee?.id) || []

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="employee-form-description">
        <DialogHeader>
          <DialogTitle id="employee-form-title">{isEditing ? '编辑员工' : '添加员工'}</DialogTitle>
          <p id="employee-form-description" className="text-sm text-slate-500">
            {isEditing ? '修改员工信息' : '填写员工基本信息'}
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                姓名 <span className="text-rose-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="请输入姓名"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">状态</label>
              <Select
                value={formData.status}
                onValueChange={(v) => handleChange('status', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择状态" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="正式">正式</SelectItem>
                  <SelectItem value="试用期">试用期</SelectItem>
                  <SelectItem value="离职">离职</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">性别</label>
              <Select
                value={formData.gender || 'unknown'}
                onValueChange={(v) => handleChange('gender', v === 'unknown' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择性别" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">请选择</SelectItem>
                  <SelectItem value="男">男</SelectItem>
                  <SelectItem value="女">女</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">出生日期</label>
              <Input
                type="date"
                value={formData.birth_date}
                onChange={(e) => handleChange('birth_date', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                部门 <span className="text-rose-500">*</span>
              </label>
              <Select
                value={formData.dept_code}
                onValueChange={(v) => handleChange('dept_code', v)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择部门" />
                </SelectTrigger>
                <SelectContent>
                  {departments?.map((dept) => (
                    <SelectItem key={dept.code} value={dept.code}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                职级 <span className="text-rose-500">*</span>
              </label>
              <Select
                value={formData.level_code}
                onValueChange={(v) => handleChange('level_code', v)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择职级" />
                </SelectTrigger>
                <SelectContent>
                  {levels?.map((level) => (
                    <SelectItem key={level.level_code} value={level.level_code}>
                      {level.level_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">汇报人</label>
              <Select
                value={formData.manager_id || 'none'}
                onValueChange={(v) => handleChange('manager_id', v === 'none' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="请选择汇报人" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">无</SelectItem>
                  {availableManagers.map((mgr) => (
                    <SelectItem key={mgr.id} value={mgr.id}>
                      {mgr.name} ({mgr.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                入职日期 <span className="text-rose-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.entry_date}
                onChange={(e) => handleChange('entry_date', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">电话</label>
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="请输入电话号码"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">邮箱</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="请输入邮箱"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
            <Button type="button" variant="outline" onClick={onClose}>
              取消
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? '提交中...' : isEditing ? '保存' : '创建'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
